<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import UiBadge from './ui/UiBadge.vue'
import PushPrompt from './PushPrompt.vue'
import { useOverlayPresence } from '../composables/useBannerSpace'
import { useEscape } from '../composables/useEscape'
import { useNotificationsStore } from '../stores/notifications'
import type { NotificationItem } from '../types/notifications'

// Колокольчик оповещений + центр истории (задача 27): счётчик непрочитанных —
// из общего пропса Inertia (unread_count, см. ApplicationController#inertia_share),
// живые оповещения — через NotificationsChannel (стор stores/notifications.ts).
// Компонент встроен в макет приложения (app/frontend/layouts/AppLayout.vue),
// поэтому виден на любой странице.
const page = usePage()
const store = useNotificationsStore()

const open = ref(false)
const panelId = useId()
const triggerEl = ref<HTMLButtonElement | null>(null)
const panelEl = ref<HTMLElement | null>(null)

// Колокольчик показывает только НЕпрочитанные: прочитанное пользователь уже
// видел (полная история — на /notifications). markRead флипает read=true →
// пункт сразу выпадает из списка; markAllRead опустошает панель.
const unreadItems = computed(() => store.items.filter((note) => !note.read))

watch(
  () => page.props.unread_count,
  (count) => store.setUnreadCount(count ?? 0),
  { immediate: true },
)

onMounted(() => {
  store.subscribe()
})

onBeforeUnmount(() => {
  store.unsubscribe()
})

function toggle(): void {
  open.value = !open.value
  if (open.value) void store.loadNotifications(10)
}

// Возврат фокуса на триггер — решение вызывающего, а не общее правило:
// уход по Tab обязан оставить фокус там, куда его увёл браузер (иначе
// клавиатурная навигация зацикливается на колокольчике), а переход по ссылке
// «Все уведомления» меняет страницу, и возвращать фокус некуда.
// Приём и его обоснование — из ui/UiMenu.vue.
function close(options: { restoreFocus?: boolean } = {}): void {
  if (!open.value) return
  open.value = false
  if (options.restoreFocus ?? true) triggerEl.value?.focus()
}

// Escape сюда намеренно не входит — за него отвечает общий реестр слоёв
// (useEscape ниже, один ключ — один обработчик). Здесь только Tab: он приходит
// на панель, потому что именно там держится фокус после того, как пользователь
// дошёл до содержимого клавиатурой.
function onPanelKeydown(event: KeyboardEvent): void {
  // Tab не перехватываем (без preventDefault): панель закрывается, а фокус
  // браузер уводит дальше по странице сам.
  if (event.key === 'Tab') close({ restoreFocus: false })
}

// Escape отдан общему реестру слоёв. Слушатель у реестра на document, а не на панели, и
// это принципиально: пока панель открыта, фокус может оставаться на кнопке колокольчика,
// и панельный слушатель Escape вообще не увидел бы — событие туда не всплывает. Слой
// активен ровно пока open.
useEscape(() => close(), () => open.value)

// Кнопка плашки предложения пуша исчезает вместе с самой плашкой сразу после
// успешного включения — вместе с ней исчезает и фокус, падая на body. Панель
// при этом остаётся открытой, и дальше с клавиатуры из неё не выйти.
// Перехватываем фокус на саму панель: она tabindex="-1" и фокусируема программно.
// nextTick обязателен: событие update:visible прилетает ДО того, как Vue
// физически уберёт кнопку плашки из DOM (watch внутри PushPrompt — pre-flush,
// выполняется раньше собственного рендера компонента), поэтому в момент
// вызова document.activeElement — ещё старая кнопка, а не body. Ждём кадр,
// в котором браузер уже перевёл фокус на body сам.
function onPromptVisible(visible: boolean): void {
  if (visible) return
  if (!open.value) return
  void nextTick(() => {
    if (document.activeElement === document.body) panelEl.value?.focus()
  })
}

// Пока панель открыта, нижние полосы молчат: панель висит прямо над полосой, и
// человек получал две одинаковые кнопки «Включить уведомления» разом — одну в
// выпадашке, вторую под ней. В дележе с ПЛАШКАМИ страницы выпадашка по-прежнему
// не участвует (см. PushPrompt.vue): она монтируется на каждый клик, и полоса
// моргала бы вместе с ней. Здесь другое: полосы не моргают, а уступают на время
// открытого слоя.
useOverlayPresence(() => open.value)

async function markAll(): Promise<void> {
  await store.markAllRead()
}

// Клик по пункту: помечаем прочитанным и, если пункт — ссылка, закрываем
// выпадашку (переход делает сам Link; фокус возвращать некуда — страница меняется).
async function onItemClick(note: NotificationItem): Promise<void> {
  if (note.url) close({ restoreFocus: false })
  await store.markRead(note.id)
}

// Ссылка на человека ведёт с панели так же, как пункт с адресом: панель закрываем
// без возврата фокуса на колокольчик, оповещение помечаем прочитанным.
function onPersonClick(note: NotificationItem): void {
  close({ restoreFocus: false })
  void store.markRead(note.id)
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString('ru-RU')
}
</script>

<template>
  <div class="relative inline-block">
    <!-- Панель под v-if: пока закрыта, элемента с id=panelId в DOM нет, и
         безусловный aria-controls указывал бы на несуществующий узел —
         значение undefined Vue снимает атрибут вовсе, а не пишет "undefined". -->
    <button
      ref="triggerEl"
      type="button"
      class="relative cursor-pointer rounded-md p-1.5 leading-none text-muted transition-colors hover:text-text"
      data-testid="bell-toggle"
      :aria-expanded="open"
      :aria-controls="open ? panelId : undefined"
      aria-label="Оповещения"
      @click="toggle"
    >
      <svg
        class="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.7 21a2 2 0 0 1-3.4 0" />
      </svg>
      <span
        v-if="store.hasUnread"
        class="absolute -right-0.5 -top-0.5 min-w-[1.1rem] rounded-full bg-crit-fill px-1 text-center text-xs leading-[1.1rem] text-white"
        data-testid="unread-badge"
      >{{ store.badgeLabel }}</span>
    </button>

    <div v-if="open" class="fixed inset-0 z-[90]" data-testid="notif-backdrop" @click="close()" />

    <!-- Роли у панели нет намеренно: это поповер с разнородным содержимым
         (кнопка «Отметить все», список, ссылка, плашка предложения пуша), а не
         меню команд — role="menu" обязывал бы всех детей быть menuitem.

         Высота панели считается от экрана, а не фиксированные 24rem: с просьбой
         включить уведомления (134px), строкой «Отметить все» и футером на список
         оставалось 145px — две карточки из шести, при том что под панелью пустовало
         308px экрана (замер на 360×740). Потолок 34rem держит поповер поповером:
         выше он читается как отдельная страница, для которой есть /notifications.
         dvh, а не vh: на мобиле адресная строка съедает высоту, и vh дал бы панель
         длиннее видимой области. -->
    <div
      v-if="open"
      :id="panelId"
      ref="panelEl"
      tabindex="-1"
      class="absolute right-0 top-[calc(100%+0.5rem)] z-[100] flex max-h-[min(34rem,calc(100dvh-5rem))] w-[min(22rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-border-strong bg-surface text-text shadow-xl"
      data-testid="notification-panel"
      @keydown="onPanelKeydown"
    >
      <!-- Человек открыл колокольчик — значит уведомления ему нужны. Здесь и
           предлагаем перенести их на телефон. Вид popover, а не plate: выпадашка
           живёт от клика до клика, и в дележе экрана с нижней полосой она не
           участвует — иначе полоса моргала бы на каждое открытие. -->
      <!-- Своего паддинга обёртка не берёт: карточка внутри PushPrompt в этом
           варианте уже с p-3, и второй слой отступов отнимал у списка ~16px ни за
           что. На низком экране (телефон в ландшафте, ~360px высоты) просьбы нет
           совсем: там она забирает под себя весь список, а спросить есть где ещё —
           плашка на «Оповещениях» и тумблер в профиле никуда не делись. -->
      <div class="shrink-0 border-b border-border [@media(max-height:520px)]:hidden">
        <PushPrompt variant="popover" @update:visible="onPromptVisible" />
      </div>

      <div v-if="store.hasUnread" class="flex shrink-0 justify-end border-b border-border p-2">
        <button
          type="button"
          class="cursor-pointer rounded-md border-none bg-transparent px-1.5 py-1 text-sm text-accent transition-colors hover:underline"
          data-testid="mark-all-read"
          @click="markAll"
        >Отметить все прочитанными</button>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto">
        <p v-if="store.loading" class="m-0 p-4 text-muted" data-testid="notification-loading">Загрузка…</p>
        <p v-else-if="!unreadItems.length" class="m-0 p-4 text-muted" data-testid="notification-empty">Новых оповещений нет.</p>

        <ul v-else class="m-0 list-none p-0">
          <li
            v-for="note in unreadItems"
            :key="note.id"
            class="relative border-t border-border transition-colors first:border-t-0 hover:bg-surface-2"
          >
            <!-- Пункт с адресом — настоящая ссылка (Inertia-Link: owner-экраны
                 рендерятся Inertia, переход идёт без перезагрузки). Без адреса
                 остаётся прежней кнопкой «прочитать»: вести такой пункт некуда.
                 Пункт растянут на всю строку через ::after; ссылка на человека лежит
                 поверх (relative z-10) — две ссылки соседями, без вложенности. -->
            <component
              :is="note.url ? Link : 'button'"
              :href="note.url || undefined"
              :type="note.url ? undefined : 'button'"
              class="flex w-full cursor-pointer flex-col gap-0.5 border-none bg-transparent px-3.5 pt-2.5 text-left text-text after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-accent"
              :class="note.person ? 'pb-1' : 'pb-2.5'"
              data-testid="notification-item"
              @click="onItemClick(note)"
            >
              <span class="flex min-w-0 items-center gap-2">
                <span class="min-w-0 flex-1 truncate" :title="note.message">{{ note.message }}</span>
                <UiBadge v-if="note.badge" tone="neutral" class="shrink-0" data-testid="notification-badge">{{ note.badge }}</UiBadge>
              </span>
              <span class="text-xs text-muted">{{ formatDate(note.created_at) }}</span>
            </component>
            <Link
              v-if="note.person"
              :href="note.person.url"
              data-testid="notification-person"
              class="relative z-10 mx-3.5 -mt-3.5 inline-block break-words rounded-sm py-3.5 text-xs font-semibold text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              @click="onPersonClick(note)"
            >{{ note.person.label }}: {{ note.person.name }} →</Link>
          </li>
        </ul>
      </div>

      <div class="shrink-0 border-t border-border p-2 text-center">
        <Link href="/notifications" class="text-sm text-accent hover:underline" @click="close({ restoreFocus: false })">Все уведомления →</Link>
      </div>
    </div>
  </div>
</template>
